import React, { useEffect, useState } from "react";
import { connectStore } from "redux-box";
import { module as wordgame } from "@st/wordgame";
import { module as ui } from "@st/ui";
import { id } from "@g/GlobalModal";
import useDebouncing from "@u/useDebouncing";
import GradeApi from "@s/api/grade";
import "../SpeakingFeaturesView.css";

const PAGE_SIZE = 20;

const levelBadgeClass = (level = "") => {
  const l = level.toLowerCase();
  return `sf-badge${l ? ` sf-badge--${l}` : ""}`;
};

function WordGameContainer({ wordgame, ui, search = "" }) {
  const [grades, setGrades] = useState([]);
  const [activeGrade, setActiveGrade] = useState(null);
  const [currentOffset, setCurrentOffset] = useState(0);

  const debouncedSearch = useDebouncing(search, 400);

  useEffect(() => {
    GradeApi.getAllGrades()
      .then((res) => {
        if (res) setGrades(res);
      })
      .catch((err) => console.error("Error loading grades:", err));
  }, []);

  const gradeTabs = [
    { label: "All", id: null },
    ...grades.map((g) => ({
      label: g.name,
      id: g.id,
    })),
  ];

  // Fetch whenever debounced search or selected grade changes — always reset to page 0.
  useEffect(() => {
    wordgame.requestWords(0, debouncedSearch || "", activeGrade || "");
    setCurrentOffset(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, activeGrade]);

  const handleGradeTab = (gradeId) => {
    setActiveGrade(gradeId);
  };

  const handlePrev = () => {
    const prev = wordgame.pagination ? wordgame.pagination.prev_page : 0;
    wordgame.requestWords(prev, debouncedSearch || "", activeGrade || "");
    setCurrentOffset(prev);
  };

  const handleNext = () => {
    const next = wordgame.pagination ? wordgame.pagination.next_page : 0;
    wordgame.requestWords(next, debouncedSearch || "", activeGrade || "");
    setCurrentOffset(next);
  };

  const openAddModal = () => {
    window.wordGameEditData = null;
    ui.showModal(id.WORDGAME_FORM_MODAL);
  };

  const handleEdit = (word) => {
    window.wordGameEditData = word;
    ui.showModal(id.WORDGAME_FORM_MODAL);
  };

  const handleDelete = (wordId) => {
    if (window.confirm("Delete this word?")) {
      wordgame.deleteWord(wordId);
    }
  };

  const playAudio = (url) => {
    if (window.sfActiveAudio) {
      window.sfActiveAudio.pause();
    }
    const audio = new Audio(url);
    audio.play().catch((err) => console.error("Audio playback failed:", err));
    window.sfActiveAudio = audio;
  };

  const pagination = wordgame.pagination;
  const hasPrev = pagination && currentOffset > 0;
  const hasNext = pagination && pagination.has_next;
  const totalCount = pagination ? pagination.count : 0;
  const dynamicPageSize = (pagination && pagination.page_size) || 25;
  const pageStart = totalCount === 0 ? 0 : currentOffset * dynamicPageSize + 1;
  const pageEnd = totalCount === 0 ? 0 : pageStart + (wordgame.words ? wordgame.words.length : 0) - 1;

  return (
    <div className="sf-inner">
      {/* ── Grade filter tabs ── */}
      <div className="sf-grade-tabs">
        {gradeTabs.map((tab) => (
          <button
            key={tab.label}
            className={`sf-grade-tab${activeGrade === tab.id ? " sf-grade-tab--active" : ""}`}
            onClick={() => handleGradeTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Table (only this scrolls) ── */}
      {wordgame.isLoading ? (
        <div className="sf-loading">Loading…</div>
      ) : (
        <div className="sf-table-wrapper">
          <table className="sf-table">
            <thead>
              <tr>
                <th>Word</th>
                <th>Level</th>
                <th>Voice Over</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {wordgame.words.length === 0 ? (
                <tr>
                  <td colSpan="4" className="sf-empty">No words found.</td>
                </tr>
              ) : (
                wordgame.words.map((w) => (
                  <tr key={w.id}>
                    <td>
                      <strong>{w.word_id ? w.word_id.word : "--"}</strong>
                    </td>
                    <td>
                      <span className={levelBadgeClass(w.word_level)}>
                        {w.word_level || "--"}
                      </span>
                    </td>
                    <td>
                      {w.word_id && w.word_id.voice_over ? (
                        <button
                          className="sf-audio-btn"
                          onClick={() => playAudio(w.word_id.voice_over)}
                        >
                          ▶ Play
                        </button>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      <button className="sf-action-btn" onClick={() => handleEdit(w)}>
                        Edit
                      </button>
                      <button
                        className="sf-action-btn sf-action-btn--delete"
                        onClick={() => handleDelete(w.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Pagination ── */}
      {!wordgame.isLoading && totalCount > 0 && (
        <div className="sf-pagination">
          <span className="sf-pagination__info">
            {pageStart}–{pageEnd} of {totalCount}
          </span>
          <button className="sf-pagination__btn" onClick={handlePrev} disabled={!hasPrev}>
            ← Prev
          </button>
          <button className="sf-pagination__btn" onClick={handleNext} disabled={!hasNext}>
            Next →
          </button>
        </div>
      )}

      {/* ── FAB ── */}
      <button id="wg-add-fab" className="sf-fab" title="Add Word" onClick={openAddModal}>
        +
      </button>
    </div>
  );
}

export default connectStore({ wordgame, ui })(WordGameContainer);
